import {
  setAtozPreScreenData,
  setCargoPreScreenData,
  setIplPreScreenData,
  setWcPreScreenData,
} from "src/jsonForms/jsonFormsSlice";
import { ISO_COUNTRY_CODES } from "src/app/constants";

import AtozPreScreen from "./AtozPreScreen";
import CargoPreScreen from "./CargoPreScreen";
import IplPreScreen from "./IplPreScreen";
import WcPreScreen from "./WcPreScreen";
import type {
  AtozPreScreenData,
  CargoPreScreenData,
  IplPreScreenData,
  LobKey,
  PreScreenLobConfig,
  WcPreScreenData,
} from "./types";

/**
 * `lineOfBusiness` value sent to `/resolveSchema` for each LOB. Shared by
 * the pre-screen builders below and the claim-number (stage-by-stage) flow
 * in `JsonFormsClaimIntakeApp`, which resolves a schema without a pre-screen.
 */
export const LINE_OF_BUSINESS_BY_LOB: Record<LobKey, string> = {
  wc: "workers-compensation",
  cargo: "cargo",
  ipl: "product-liability",
  atoz: "atoz",
};

const WC_LOB_CONFIG: PreScreenLobConfig<WcPreScreenData> = {
  lobKey: "wc",
  PreScreenComponent: WcPreScreen,
  buildResolveSchemaInput: (data) => ({
    lineOfBusiness: LINE_OF_BUSINESS_BY_LOB.wc,
    options: {
      country: data.incidentCountry ?? ISO_COUNTRY_CODES.UNITED_STATES,
      ...(data.incidentState != null && { state: data.incidentState }),
      incidentDate: data.incidentDate,
    },
  }),
  selectPreScreenData: (state) => state.jsonForms.preScreen.wc,
  saveAction: setWcPreScreenData,
  // Forward every captured associate / incident selection onto the
  // claim; `resolvedTpa` is a schema-routing artifact and stays off the
  // payload.
  buildClaimContext: (data) => ({
    associateEmployeeId: data.associateEmployeeId,
    associateAlias: data.associateAlias,
    associateFirstName: data.associateFirstName,
    associateLastName: data.associateLastName,
    associateHomeSiteCode: data.associateHomeSiteCode,
    workerSubType: data.workerSubType,
    supervisorAlias: data.supervisorAlias,
    supervisorName: data.supervisorName,
    incidentAtHomeSite: data.incidentAtHomeSite,
    locationType: data.locationType,
    incidentSiteCode: data.incidentSiteCode,
    incidentDate: data.incidentDate,
    incidentTime: data.incidentTime,
  }),
};

const CARGO_LOB_CONFIG: PreScreenLobConfig<CargoPreScreenData> = {
  lobKey: "cargo",
  PreScreenComponent: CargoPreScreen,
  buildResolveSchemaInput: ({ businessUnit }) => ({
    lineOfBusiness: LINE_OF_BUSINESS_BY_LOB.cargo,
    options: {
      businessUnit,
    },
  }),
  selectPreScreenData: (state) => state.jsonForms.preScreen.cargo,
  saveAction: setCargoPreScreenData,
  // Pass the user-facing business unit onto the claim; `schemaSegment`
  // is a schema-routing artifact and stays off the payload.
  buildClaimContext: ({ businessUnit }) => ({ businessUnit }),
};

const IPL_LOB_CONFIG: PreScreenLobConfig<IplPreScreenData> = {
  lobKey: "ipl",
  PreScreenComponent: IplPreScreen,
  buildResolveSchemaInput: () => ({
    lineOfBusiness: LINE_OF_BUSINESS_BY_LOB.ipl,
    options: {},
  }),
  selectPreScreenData: (state) => state.jsonForms.preScreen.ipl,
  saveAction: setIplPreScreenData,
  // Qualification answers are routing artifacts and stay off the payload.
  buildClaimContext: () => ({}),
};

const ATOZ_LOB_CONFIG: PreScreenLobConfig<AtozPreScreenData> = {
  lobKey: "atoz",
  PreScreenComponent: AtozPreScreen,
  buildResolveSchemaInput: ({ country }) => ({
    lineOfBusiness: LINE_OF_BUSINESS_BY_LOB.atoz,
    options: {
      country,
    },
  }),
  selectPreScreenData: (state) => state.jsonForms.preScreen.atoz,
  saveAction: setAtozPreScreenData,
  buildClaimContext: ({ country }) => ({ country }),
};

/**
 * Discriminated union of every LOB's `PreScreenLobConfig`. The shell
 * narrows by `lobKey` before invoking `buildResolveSchemaInput` /
 * `selectPreScreenData` / `saveAction`, so each callback always sees
 * the matching `TData` shape without needing a cast.
 */
export type AnyPreScreenLobConfig =
  | PreScreenLobConfig<WcPreScreenData>
  | PreScreenLobConfig<CargoPreScreenData>
  | PreScreenLobConfig<IplPreScreenData>
  | PreScreenLobConfig<AtozPreScreenData>;

/**
 * The single LOB registry consumed by `JsonFormsClaimIntakeApp`. Adding
 * a new LOB requires one new entry here, one new pre-screen component,
 * and a new `LOB_ROLES` entry in `lobAccess.ts`.
 */
export const LOB_CONFIGS: Record<LobKey, AnyPreScreenLobConfig> = {
  wc: WC_LOB_CONFIG,
  cargo: CARGO_LOB_CONFIG,
  ipl: IPL_LOB_CONFIG,
  atoz: ATOZ_LOB_CONFIG,
};
